import { useEffect, useState } from 'react';
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TableFooter,
  MenuItem,
  Select,
  useTheme as useMuiTheme,
  alpha,
  Tooltip,
  FormControl,
  InputLabel,
  Box,
} from '@mui/material';
import {
  useGetTotalFinalPriceCloseouts,
  useGetTotalFinalPriceChangeOrderCloseouts,
} from '../../hooks/useCloseoutHooks';
import { useStore } from 'zustand';
import { useUnityBuildStore } from '../../stores/store';
import { formatCurrency, formatNumber, safeDivide } from './closeoutHelpers';
import { BreakoutAllocation } from '../../api/protosCompiled/estimateCloseout/estimateCloseout_pb';
import {
  useFetchEstimateBreakouts,
  useFetchChangeOrderBreakouts,
} from '../../hooks/useFetchBreakout';

export type AggregatedExtensionData = {
  breakoutId: string;
  breakoutName: string;
  totalCost: number;
  totalLaborHours: number;
};

type FooterTotals = {
  totalLaborHours: number;
};

type AllocationMethod = 'percentage' | 'fixed' | 'default';

export const ScheduleOfValues = () => {
  const muiTheme = useMuiTheme();

  // Common styles matching TotalPricingRow and TotalPricingHeader
  const headerCellStyle = {
    fontWeight: 600,
    backgroundColor:
      muiTheme.palette.mode === 'dark' ? muiTheme.palette.background.paper : '#f5f5f5',
    color: muiTheme.palette.text.primary,
    fontSize: '0.875rem',
    padding: muiTheme.spacing(1.5, 2),
    whiteSpace: 'nowrap' as const,
    borderBottom: `1px solid ${muiTheme.palette.divider}`,
  };

  const rowStyle = {
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor:
        muiTheme.palette.mode === 'dark'
          ? alpha(muiTheme.palette.common.white, 0.1)
          : alpha(muiTheme.palette.common.black, 0.04),
    },
    '&:nth-of-type(even)': {
      backgroundColor:
        muiTheme.palette.mode === 'dark'
          ? alpha(muiTheme.palette.common.white, 0.02)
          : alpha(muiTheme.palette.common.black, 0.02),
    },
  };

  const cellStyle = {
    color: muiTheme.palette.text.primary,
    padding: muiTheme.spacing(1.5, 2),
    borderBottom: `1px solid ${muiTheme.palette.divider}`,
    whiteSpace: 'nowrap' as const,
  };

  const footerRowStyle = {
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor:
        muiTheme.palette.mode === 'dark'
          ? alpha(muiTheme.palette.common.white, 0.1)
          : alpha(muiTheme.palette.common.black, 0.04),
    },
    '&:nth-of-type(even)': {
      backgroundColor:
        muiTheme.palette.mode === 'dark'
          ? alpha(muiTheme.palette.common.white, 0.02)
          : alpha(muiTheme.palette.common.black, 0.02),
    },
  };

  const footerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
  };
  const selectedEstimate = useStore(useUnityBuildStore).selectedEstimate;
  const selectedChangeOrder = useStore(useUnityBuildStore).selectedChangeOrder;
  const [aggregatedExtensionData, setAggregatedExtensionData] = useState<AggregatedExtensionData[]>(
    []
  );

  // Get data with refetch capabilities
  const { data: estimatecloseouts } = useGetTotalFinalPriceCloseouts();

  const { data: changeOrdercloseouts } = useGetTotalFinalPriceChangeOrderCloseouts();

  // Fetch breakouts to get all available breakouts
  const { data: estimateBreakouts } = useFetchEstimateBreakouts(selectedEstimate?.estimateid ?? '');
  const { data: changeOrderBreakouts } = useFetchChangeOrderBreakouts(
    selectedChangeOrder?.changeorderid ?? ''
  );

  const [allocationMethodValue, setAllocationMethodValue] = useState<AllocationMethod>('fixed');
  const [forceUpdate, setForceUpdate] = useState(false);

  const [totalsFullyAllocated, setTotalsFullyAllocated] = useState<boolean>(false);

  const closeouts = selectedEstimate?.estimateid ? estimatecloseouts : changeOrdercloseouts;
  const breakouts = selectedEstimate?.estimateid ? estimateBreakouts : changeOrderBreakouts;

  useEffect(() => {
    if (
      closeouts &&
      (!hasValidAllocation(
        closeouts.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList
      ) ||
        !hasValidAllocation(
          closeouts.closeouts?.incidentallaborcloseout?.incidentallaborcloseoutList
        ) ||
        !hasValidAllocation(
          closeouts.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList
        ) ||
        !hasValidAllocation(closeouts.closeouts?.equipmentcloseout?.equipmentcloseoutList) ||
        !hasValidAllocation(
          closeouts.closeouts?.generalexpensecloseout?.generalexpensecloseoutList
        ) ||
        !hasValidAllocation(closeouts.closeouts?.subcontractcloseout?.subcontractcloseoutList) ||
        !hasValidAllocation(closeouts.closeouts?.quotecloseout?.quotecloseoutList))
    ) {
      setTotalsFullyAllocated(false);
    } else {
      setTotalsFullyAllocated(true);
    }
    //eslint-disable-next-line
  }, [closeouts]);

  // Helper function to check if allocation is valid
  const hasValidAllocation = (closeoutList: any) => {
    if (!closeoutList || closeoutList.length === 0) return true; // No allocations needed
    return isApproximately100(closeoutList[0]?.allocationList ?? []);
  };

  // Helper function to check if sum is approximately 100
  const isApproximately100 = (allocationList: BreakoutAllocation.AsObject[]) => {
    if (!allocationList || allocationList.length === 0) return false;

    const sum = allocationList.reduce((sum, data) => sum + data.allocationpercentage, 0);

    // Allow small floating point errors
    return Math.abs(sum - 100) < 0.01; // Adjust tolerance as needed
  };

  useEffect(() => {
    if (closeouts && breakouts) {
      const breakoutMap: Record<string, AggregatedExtensionData> = {};

      // First, add all breakouts from the breakouts list
      if (breakouts.breakoutsList) {
        breakouts.breakoutsList.forEach(breakout => {
          const breakoutName = breakout.breakoutname || 'Unknown';
          const breakoutId = breakout.id || '';

          if (!breakoutMap[breakoutName]) {
            breakoutMap[breakoutName] = {
              breakoutId,
              breakoutName,
              totalCost: 0,
              totalLaborHours: 0,
            };
          }
        });
      }

      // Then, add extension data for breakouts that have items
      if (closeouts?.closeouts?.extendestimate?.extendestimateList) {
        const extensionList = closeouts.closeouts.extendestimate.extendestimateList;

        extensionList.forEach(ext => {
          const breakoutName = ext.breakoutname || 'Unknown';
          const breakoutId = ext.breakoutid || '';

          if (!breakoutMap[breakoutName]) {
            breakoutMap[breakoutName] = {
              breakoutId,
              breakoutName,
              totalCost: 0,
              totalLaborHours: 0,
            };
          }

          breakoutMap[breakoutName].totalCost += ext.totalcost || 0;
          breakoutMap[breakoutName].totalLaborHours += ext.totaldirectlaborhours || 0;
        });
      }

      const aggregatedData = Object.values(breakoutMap).sort((a, b) =>
        a.breakoutName.localeCompare(b.breakoutName)
      );
      setAggregatedExtensionData(aggregatedData);
    }
  }, [
    closeouts,
    breakouts,
    estimatecloseouts,
    changeOrdercloseouts,
    allocationMethodValue,
    forceUpdate,
  ]);

  const totalLaborHours: FooterTotals = aggregatedExtensionData.reduce(
    (totals, data) => {
      totals.totalLaborHours += data.totalLaborHours;
      return totals;
    },
    { totalLaborHours: 0 }
  );

  const allocationPercentage = (
    data: AggregatedExtensionData,
    totalCosts: number[],
    allocationMethod: AllocationMethod,
    allocationList: BreakoutAllocation.AsObject[]
  ) => {
    if (!totalCosts || totalCosts.length === 0) {
      return { directLaborPercentage: 0, allocatedLaborHours: 0 };
    }

    let percentage = 0;
    let allocatedValue = 0;
    const totalCost = totalCosts.reduce((sum, value) => sum + value, 0);

    if (allocationMethod === 'percentage' || allocationMethod === 'default') {
      percentage = safeDivide(data.totalLaborHours, totalCosts[0]);
      allocatedValue =
        data.totalLaborHours * safeDivide(totalCosts[0], totalLaborHours.totalLaborHours);
    } else if (allocationMethod === 'fixed' && allocationList.length > 0) {
      const percent =
        allocationList.find(alloc => alloc.breakoutid === data.breakoutId)?.allocationpercentage ||
        0;
      percentage = safeDivide(percent, 100);
      allocatedValue = totalCost * percentage;
    }

    return {
      directLaborPercentage: percentage * 100, // Convert back to percentage for display
      allocatedValue: allocatedValue,
    };
  };

  const sumTotal = (values: number[]) => {
    return values.reduce((sum, data) => sum + data, 0);
  };

  const handleAllocationChange = (e: any) => {
    setAllocationMethodValue(e.target.value as AllocationMethod);
    setForceUpdate(prev => !prev); // Toggle boolean state to force re-render
  };

  const calculateMaterialCostForBreakout = (breakoutId: string) => {
    // Examine each entry of extendestimateList and sum the total as a map of breakoutId to total cost
    const breakouts =
      closeouts?.closeouts?.extendestimate?.extendestimateList.filter(
        b => b.breakoutid === breakoutId
      ) || [];

    if (!breakouts || breakouts.length === 0) {
      return 0; // Return 0 for breakouts without extension items
    }

    const breakoutMap: Record<string, number> = {};
    breakouts.forEach(b => {
      if (!breakoutMap[b.breakoutid]) {
        breakoutMap[b.breakoutid] = 0;
      }
      breakoutMap[b.breakoutid] += b.totalcost || 0;
    });

    // Get the sum of the total cost for all breakouts
    const sumTotalCost =
      closeouts?.closeouts?.extendestimate?.extendestimateList.reduce(
        (sum, b) => sum + (b.totalcost || 0),
        0
      ) || 0;

    if (sumTotalCost === 0) {
      return 0;
    }

    // Get the total cost from the closeout object
    const totalCost = closeouts?.closeouts?.extendestimate?.totalcost || 0;

    // Get the multiplier to apply to the breakoutMap
    const multiplier = safeDivide(totalCost, sumTotalCost);

    // Multiply the breakoutMap by the multiplier
    const value = Object.values(breakoutMap).reduce((sum, b) => sum + b * multiplier, 0);

    return value || 0;
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: muiTheme.palette.background.default,
      }}
    >
      <Box sx={{ margin: '0 auto 16px', width: '250px' }}>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel
            id="allocation-method-label"
            sx={{
              color: muiTheme.palette.text.secondary,
              '&.Mui-focused': {
                color: muiTheme.palette.primary.main,
              },
            }}
          >
            Allocation Method
          </InputLabel>
          <Select
            fullWidth
            labelId="allocation-method-label"
            id="allocation-method-select"
            value={allocationMethodValue}
            onChange={handleAllocationChange}
            label="Allocation Method"
            aria-label="Choose allocation method"
            aria-description="Select whether to allocate costs by percentage of direct labor or by fixed allocations"
            sx={{
              backgroundColor: muiTheme.palette.background.paper,
              color: muiTheme.palette.text.primary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: muiTheme.palette.divider,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: muiTheme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: muiTheme.palette.primary.main,
              },
              '& .MuiSelect-icon': {
                color: muiTheme.palette.text.secondary,
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: muiTheme.palette.background.paper,
                  color: muiTheme.palette.text.primary,
                  '& .MuiMenuItem-root': {
                    color: muiTheme.palette.text.primary,
                  },
                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.15),
                    color: muiTheme.palette.primary.main,
                  },
                  '& .MuiMenuItem-root:hover': {
                    backgroundColor: alpha(muiTheme.palette.action.hover, 0.1),
                  },
                },
              },
            }}
          >
            <MenuItem value="percentage">% of Direct Labor</MenuItem>
            <MenuItem value="fixed">Fixed Allocations</MenuItem>
          </Select>
        </FormControl>
      </Box>
      {!totalsFullyAllocated && allocationMethodValue === 'fixed' && (
        <Typography
          variant="body2"
          align="center"
          gutterBottom
          sx={{
            fontWeight: 'bold',
            color: muiTheme.palette.error.main,
            backgroundColor: alpha(muiTheme.palette.error.main, 0.1),
            padding: muiTheme.spacing(1),
            borderRadius: muiTheme.shape.borderRadius,
            mb: 2,
          }}
        >
          Some closeouts are over or under allocated. Select "Allocations" at the bottom of the page
          to allocate closeouts.
        </Typography>
      )}
      <TableContainer
        component={Paper}
        sx={{
          backgroundColor: muiTheme.palette.background.default,
          borderColor: muiTheme.palette.divider,
          '& .MuiPaper-root': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Name of the breakout section" arrow>
                  <span>Breakout Name</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Total estimate or selling price for this breakout" arrow>
                  <span>Total Estimate / Selling Price</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Material cost for this breakout" arrow>
                  <span>Material Cost</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Direct labor percentage for this breakout" arrow>
                  <span>Direct Labor %</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Direct labor hours for this breakout" arrow>
                  <span>Direct Labor Hours</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Labor factoring hours for this breakout" arrow>
                  <span>Labor Factoring Hours</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Incidental labor hours for this breakout" arrow>
                  <span>Incidental Labor Hours</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Indirect labor hours for this breakout" arrow>
                  <span>Indirect Labor Hours</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Direct labor cost for this breakout" arrow>
                  <span>Direct Labor Cost</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Labor factoring cost for this breakout" arrow>
                  <span>Labor Factoring Cost</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Incidental labor cost for this breakout" arrow>
                  <span>Incidental Labor Cost</span>
                </Tooltip>
              </TableCell>
              <TableCell sx={headerCellStyle}>
                <Tooltip title="Indirect labor cost for this breakout" arrow>
                  <span>Indirect Labor Cost</span>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                  fontWeight: 600,
                  borderColor: 'divider',
                  p: 1,
                }}
              >
                <Tooltip title="Equipment cost for this breakout" arrow>
                  <span>Equipment Cost</span>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                  fontWeight: 600,
                  borderColor: 'divider',
                  p: 1,
                }}
              >
                <Tooltip title="General expense cost for this breakout" arrow>
                  <span>General Expense Cost</span>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                  fontWeight: 600,
                  borderColor: 'divider',
                  p: 1,
                }}
              >
                <Tooltip title="Subcontract cost for this breakout" arrow>
                  <span>Subcontract Cost</span>
                </Tooltip>
              </TableCell>
              <TableCell
                sx={{
                  backgroundColor: 'background.paper',
                  color: 'text.primary',
                  fontWeight: 600,
                  borderColor: 'divider',
                  p: 1,
                }}
              >
                <Tooltip title="Quote cost for this breakout" arrow>
                  <span>Quote Cost</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {aggregatedExtensionData.map((data, index) => {
              // Calculate values with proper defaults to prevent NaN
              const directLaborPercentage =
                allocationPercentage(data, [totalLaborHours.totalLaborHours || 0], 'default', [])
                  .directLaborPercentage || 0;

              return (
                <TableRow key={index} sx={rowStyle}>
                  <TableCell sx={cellStyle}>{data.breakoutName || 'Unknown'}</TableCell>
                  <TableCell sx={cellStyle}>
                    {allocationMethodValue === 'percentage'
                      ? formatCurrency(
                          allocationPercentage(
                            data,
                            [closeouts?.closeouts?.totalfinalprice || 0],
                            allocationMethodValue,
                            []
                          ).allocatedValue || 0
                        )
                      : formatCurrency(
                          sumTotal([
                            calculateMaterialCostForBreakout(data.breakoutId) || 0,
                            allocationPercentage(
                              data,
                              [closeouts?.closeouts?.directlaborcloseout?.totalcost || 0],
                              'default',
                              []
                            ).allocatedValue || 0,
                            allocationPercentage(
                              data,
                              [closeouts?.closeouts?.laborfactoringcloseout?.totalcost || 0],
                              allocationMethodValue,
                              closeouts?.closeouts?.laborfactoringcloseout
                                ?.laborfactoringcloseoutList[0]?.allocationList || []
                            ).allocatedValue || 0,
                            allocationPercentage(
                              data,
                              [closeouts?.closeouts?.incidentallaborcloseout?.totalcost || 0],
                              allocationMethodValue,
                              closeouts?.closeouts?.incidentallaborcloseout
                                ?.incidentallaborcloseoutList[0]?.allocationList || []
                            ).allocatedValue || 0,
                            allocationPercentage(
                              data,
                              [closeouts?.closeouts?.indirectlaborcloseout?.totalcost || 0],
                              allocationMethodValue,
                              closeouts?.closeouts?.indirectlaborcloseout
                                ?.indirectlaborcloseoutList[0]?.allocationList || []
                            ).allocatedValue || 0,
                            allocationPercentage(
                              data,
                              [closeouts?.closeouts?.equipmentcloseout?.totalcost || 0],
                              allocationMethodValue,
                              closeouts?.closeouts?.equipmentcloseout?.equipmentcloseoutList[0]
                                ?.allocationList || []
                            ).allocatedValue || 0,
                            allocationPercentage(
                              data,
                              [closeouts?.closeouts?.generalexpensecloseout?.totalcost || 0],
                              allocationMethodValue,
                              closeouts?.closeouts?.generalexpensecloseout
                                ?.generalexpensecloseoutList[0]?.allocationList || []
                            ).allocatedValue || 0,
                            allocationPercentage(
                              data,
                              [closeouts?.closeouts?.subcontractcloseout?.totalcost || 0],
                              allocationMethodValue,
                              closeouts?.closeouts?.subcontractcloseout?.subcontractcloseoutList[0]
                                ?.allocationList || []
                            ).allocatedValue || 0,
                            allocationPercentage(
                              data,
                              [closeouts?.closeouts?.quotecloseout?.totalcost || 0],
                              allocationMethodValue,
                              closeouts?.closeouts?.quotecloseout?.quotecloseoutList[0]
                                ?.allocationList || []
                            ).allocatedValue || 0,
                          ])
                        )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatCurrency(calculateMaterialCostForBreakout(data.breakoutId) || 0)}
                  </TableCell>
                  <TableCell sx={cellStyle}>{directLaborPercentage.toFixed(2)}%</TableCell>
                  <TableCell sx={cellStyle}>
                    {formatNumber(
                      allocationPercentage(
                        data,
                        [
                          closeouts?.closeouts?.directlaborcloseout?.directlaborcloseoutList.reduce(
                            (sum, data) => sum + data.allocatedhours,
                            0
                          ) || 0,
                        ],
                        'default',
                        []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatNumber(
                      allocationPercentage(
                        data,
                        [
                          closeouts?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList.reduce(
                            (sum, data) => sum + data.allocatedhours,
                            0
                          ) || 0,
                        ],
                        allocationMethodValue,
                        closeouts?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList[0]
                          ?.allocationList || []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatNumber(
                      allocationPercentage(
                        data,
                        [
                          closeouts?.closeouts?.incidentallaborcloseout?.incidentallaborcloseoutList.reduce(
                            (sum, data) => sum + data.allocatedhours,
                            0
                          ) || 0,
                        ],
                        allocationMethodValue,
                        closeouts?.closeouts?.incidentallaborcloseout
                          ?.incidentallaborcloseoutList[0]?.allocationList || []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatNumber(
                      allocationPercentage(
                        data,
                        [
                          closeouts?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList.reduce(
                            (sum, data) => sum + data.allocatedhours,
                            0
                          ) || 0,
                        ],
                        allocationMethodValue,
                        closeouts?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList[0]
                          ?.allocationList || []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatCurrency(
                      allocationPercentage(
                        data,
                        [closeouts?.closeouts?.directlaborcloseout?.totalcost || 0],
                        'default',
                        []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatCurrency(
                      allocationPercentage(
                        data,
                        [closeouts?.closeouts?.laborfactoringcloseout?.totalcost || 0],
                        allocationMethodValue,
                        closeouts?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList[0]
                          ?.allocationList || []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatCurrency(
                      allocationPercentage(
                        data,
                        [closeouts?.closeouts?.incidentallaborcloseout?.totalcost || 0],
                        allocationMethodValue,
                        closeouts?.closeouts?.incidentallaborcloseout
                          ?.incidentallaborcloseoutList[0]?.allocationList || []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatCurrency(
                      allocationPercentage(
                        data,
                        [closeouts?.closeouts?.indirectlaborcloseout?.totalcost || 0],
                        allocationMethodValue,
                        closeouts?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList[0]
                          ?.allocationList || []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatCurrency(
                      allocationPercentage(
                        data,
                        [closeouts?.closeouts?.equipmentcloseout?.totalcost || 0],
                        allocationMethodValue,
                        closeouts?.closeouts?.equipmentcloseout?.equipmentcloseoutList[0]
                          ?.allocationList || []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatCurrency(
                      allocationPercentage(
                        data,
                        [closeouts?.closeouts?.generalexpensecloseout?.totalcost || 0],
                        allocationMethodValue,
                        closeouts?.closeouts?.generalexpensecloseout?.generalexpensecloseoutList[0]
                          ?.allocationList || []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatCurrency(
                      allocationPercentage(
                        data,
                        [closeouts?.closeouts?.subcontractcloseout?.totalcost || 0],
                        allocationMethodValue,
                        closeouts?.closeouts?.subcontractcloseout?.subcontractcloseoutList[0]
                          ?.allocationList || []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                  <TableCell sx={cellStyle}>
                    {formatCurrency(
                      allocationPercentage(
                        data,
                        [closeouts?.closeouts?.quotecloseout?.totalcost || 0],
                        allocationMethodValue,
                        closeouts?.closeouts?.quotecloseout?.quotecloseoutList[0]?.allocationList ||
                          []
                      ).allocatedValue || 0
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow sx={footerRowStyle}>
              <TableCell sx={footerCellStyle}>Totals</TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(
                  closeouts?.closeouts?.sellingprice || closeouts?.closeouts?.totalfinalprice || 0
                )}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(closeouts?.closeouts?.extendestimate?.totalcost || 0)}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {totalLaborHours.totalLaborHours.toFixed(2)}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatNumber(
                  closeouts?.closeouts?.directlaborcloseout?.directlaborcloseoutList.reduce(
                    (sum, data) => sum + data.allocatedhours,
                    0
                  ) || 0
                )}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatNumber(
                  closeouts?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList.reduce(
                    (sum, data) => sum + data.allocatedhours,
                    0
                  ) || 0
                )}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatNumber(
                  closeouts?.closeouts?.incidentallaborcloseout?.incidentallaborcloseoutList.reduce(
                    (sum, data) => sum + data.allocatedhours,
                    0
                  ) || 0
                )}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatNumber(
                  closeouts?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList.reduce(
                    (sum, data) => sum + data.allocatedhours,
                    0
                  ) || 0
                )}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(closeouts?.closeouts?.directlaborcloseout?.totalcost || 0)}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(closeouts?.closeouts?.laborfactoringcloseout?.totalcost || 0)}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(closeouts?.closeouts?.incidentallaborcloseout?.totalcost || 0)}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(closeouts?.closeouts?.indirectlaborcloseout?.totalcost || 0)}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(closeouts?.closeouts?.equipmentcloseout?.totalcost || 0)}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(closeouts?.closeouts?.generalexpensecloseout?.totalcost || 0)}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(closeouts?.closeouts?.subcontractcloseout?.totalcost || 0)}
              </TableCell>
              <TableCell sx={footerCellStyle}>
                {formatCurrency(closeouts?.closeouts?.quotecloseout?.totalcost || 0)}
              </TableCell>
            </TableRow>
            <TableRow sx={footerRowStyle}>
              <TableCell sx={footerCellStyle}>Total Labor Hours:</TableCell>
              <TableCell sx={footerCellStyle}>
                {formatNumber(
                  sumTotal([
                    closeouts?.closeouts?.directlaborcloseout?.directlaborcloseoutList.reduce(
                      (sum, data) => sum + data.allocatedhours,
                      0
                    ) || 0,
                    closeouts?.closeouts?.laborfactoringcloseout?.laborfactoringcloseoutList.reduce(
                      (sum, data) => sum + data.allocatedhours,
                      0
                    ) || 0,
                    closeouts?.closeouts?.incidentallaborcloseout?.incidentallaborcloseoutList.reduce(
                      (sum, data) => sum + data.allocatedhours,
                      0
                    ) || 0,
                    closeouts?.closeouts?.indirectlaborcloseout?.indirectlaborcloseoutList.reduce(
                      (sum, data) => sum + data.allocatedhours,
                      0
                    ) || 0,
                  ])
                )}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};
